import * as React from 'react';
import { cn } from '@/utils/cn';
import { Label } from '@/components/ui/label';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  success?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, success, type = 'text', id, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;
    const feedbackId = `${inputId}-feedback`;
    const describedBy =
      [props['aria-describedby'], (error || success) && feedbackId].filter(Boolean).join(' ') ||
      undefined;

    return (
      <div className="w-full">
        {label && (
          <Label htmlFor={inputId} className="mb-2 block text-sm font-medium text-content-secondary">
            {label}
          </Label>
        )}
        <input
          {...props}
          id={inputId}
          type={type}
          aria-describedby={describedBy}
          aria-invalid={Boolean(error)}
          className={cn(
            'flex min-h-11 w-full rounded-control border bg-paper px-4 py-2.5 font-ui text-base text-content-primary transition-[border-color,box-shadow] duration-200 placeholder:text-content-tertiary focus:outline-none focus:ring-2 focus:ring-ring/20 focus:ring-offset-2 focus:ring-offset-parchment disabled:cursor-not-allowed disabled:opacity-45',
            error
              ? 'border-state-critical focus:border-state-critical focus:ring-state-critical/20'
              : success
                ? 'border-state-nominal focus:border-state-nominal focus:ring-state-nominal/20'
                : 'border-hairline focus:border-ink',
            className
          )}
          ref={ref}
        />
        {error && (
          <p id={feedbackId} role="alert" className="mt-1.5 text-sm text-state-critical">
            {error}
          </p>
        )}
        {success && (
          <p id={feedbackId} className="mt-1.5 text-sm text-state-nominal">
            {success}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
